import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import interactiveRegistry from "../Registry/Interactive/InteractiveRegistry";
import { createScopedStorage } from "./workspaceStore";
import { getSidebarFavoritesKey } from "../workspace/constants";

const getMasterOrder = () => {
  // Flatten registry interactives in master order by category then entry key
  return Object.entries(interactiveRegistry).flatMap(([, cat]) =>
    Object.entries(cat.interactives).map(
      ([typeKey, entry]) => entry.key || typeKey,
    ),
  );
};

// Default favorites - 16 highest priority tools from MVP gap analysis
// P0 (Critical) + P1 (High Priority) + P2 (Medium Priority) tools that cover most common use cases
const DEFAULT_FAVORITES = [
  // P0 - Critical tools (most essential)
  "fibRetracement", // Fibonacci Retracement (most critical Fibonacci tool)
  "trendLine", // Trend Line (most basic trend analysis)
  "horizontalLine", // Horizontal Line (price levels)
  "interactiveText", // Text Annotation (labeling)
  "equidistantChannel", // Channel (trend channels)
  "rectangle", // Rectangle (support/resistance boxes)

  // P1 - High priority tools
  "ray", // Ray (extended trend lines)
  "segment", // Segment (price measurements)
  "verticalLine", // Vertical Line (time markers)
  "fibExtension", // Fibonacci Extension (3-point projection)
  "ellipse", // Ellipse/Circle (versatile shape)
  "andrewsPitchfork", // Andrews Pitchfork (professional channel tool)

  // P2 - Medium priority tools (recently implemented, high utility)
  "fibProjection", // Fibonacci Projection (price targets)
  "arrow", // Arrow (directional marker)
  "callout", // Callout (text balloon)
  "line", // Infinite Line (extended trend)
];

export const useSidebarFavoritesStore = create(
  persist(
    (set, get) => ({
      favorites: DEFAULT_FAVORITES, // Pre-populated with 8 highest priority tools
      sidebarOpen: true,
      addFavorite: (interactiveKey) =>
        set((state) => {
          if (state.favorites.includes(interactiveKey)) {
            return { favorites: state.favorites };
          }
          return { favorites: [...state.favorites, interactiveKey] };
        }),
      removeFavorite: (interactiveKey) =>
        set((state) => ({
          favorites: state.favorites.filter((k) => k !== interactiveKey),
        })),
      toggleFavorite: (interactiveKey) =>
        set((state) => {
          if (state.favorites.includes(interactiveKey)) {
            return {
              favorites: state.favorites.filter((k) => k !== interactiveKey),
            };
          }
          return { favorites: [...state.favorites, interactiveKey] };
        }),
      clearFavorites: () => set({ favorites: [] }),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      toggleSidebar: () => {
        set((state) => {
          return { sidebarOpen: !state.sidebarOpen };
        });
      },
      getSortedFavorites: () => {
        const masterOrder = getMasterOrder();
        // Map favorite names to objects, sort by master order
        const favKeys = get().favorites;
        const allEntries = Object.entries(interactiveRegistry).flatMap(
          ([_categoryKey, cat]) =>
            Object.entries(cat.interactives).map(([typeKey, entry]) => ({
              key: entry.key || typeKey,
              entry,
              categoryKey: _categoryKey,
            })),
        );
        return masterOrder
          .filter((k) => favKeys.includes(k))
          .map((k) => allEntries.find((e) => e.key === k))
          .filter(Boolean);
      },
    }),
    {
      name: "sidebar-favorites", // Used as fallback name, actual key is dynamic (user-scoped)
      storage: createJSONStorage(() =>
        createScopedStorage(getSidebarFavoritesKey),
      ),
      merge: (persistedState, currentState) => {
        // Custom merge function to handle empty favorites
        const merged = { ...currentState, ...persistedState };

        // If favorites is empty after merge, use defaults
        if (!merged.favorites || merged.favorites.length === 0) {
          merged.favorites = DEFAULT_FAVORITES;
        }

        return merged;
      },
    },
  ),
);

// Listen for workspace context changes and rehydrate the store
if (typeof window !== "undefined") {
  window.addEventListener("workspace-context-changed", () => {
    console.log("[SidebarFavoritesStore] Rehydrating after context change");
    useSidebarFavoritesStore.persist.rehydrate();
  });
}
